import { v4 as uuid } from 'uuid';
import { create } from 'zustand';

import {
  API_KEY_STORAGE_KEY,
  DEFAULT_MODEL,
  MODEL_STORAGE_KEY,
} from '@/components/settings/SettingsDrawer';
import { ArtifactExtractor } from '@/lib/artifact';
import { STREAMING_SCAFFOLD } from '@/lib/canvas-host';
import { db } from '@/lib/db';
import { buildApiMessages, tryParseAnthropicError } from '@/lib/api-messages';
import { EDIT_MODE_DIRECTIVE } from '@/lib/directives';
import { applyEdits, extractEditsBlock, parseEdits, stripEditsBlock } from '@/lib/edits';
import { isBundlerHtml, unbundleHtml } from '@/lib/import-bundler';
import { buildImportNote, mergeImportedHtml } from '@/lib/import-merge';
import { selfContainHtml } from '@/lib/import-self-contain';
import { extractTweakManifest } from '@/lib/tweaks';
import type {
  Comment as CommentRow,
  Edit as EditRow,
  Message,
  Project,
  SavedFile,
  TweakValue,
  Version,
} from '@/lib/schemas';
import {
  streamTextDeltas,
  StreamError,
  ProxyUnreachableError,
} from '@/lib/stream';
import { SYSTEM_PROMPT_V1 } from '@/lib/system-prompt';

export type ChatRole = Message['role'];
export type ChatStatus = 'pending' | 'streaming' | 'complete' | 'error' | 'cancelled' | 'partial';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: number;
  status: ChatStatus;
  versionId: string | null;
  /** Attachments rendered alongside the user's text in the bubble. In-memory only
   *  for now — not persisted to IndexedDB, so they don't appear after a page reload. */
  attachments?: Array<
    | { kind: 'image'; dataUrl: string; mediaType: string }
    | { kind: 'document'; name: string; bytes: number }
    | { kind: 'text-file'; name: string; mediaType: string; bytes: number }
  >;
}

export type DeviceFrame = 'desktop' | 'tablet' | 'mobile';
export type CanvasMode = 'select' | 'comment' | 'edit' | 'tweaks' | 'code';

export interface PendingComment {
  selector: string;
  snippet: string;
  rect: { x: number; y: number; width: number; height: number };
}

export interface StreamProgress {
  startedAt: number;
  lastBytesAt: number;
  totalBytes: number;
  /** Model actually being called (may differ from user-chosen default — edits use Sonnet). */
  model: string;
  /** Set when the watchdog auto-aborts because no upstream bytes arrived for too long. */
  watchdogTrippedReason?: string;
}

/**
 * Attachments cover three model-friendly file flavors:
 *   - `image`: PNG/JPEG/GIF/WebP, sent as Anthropic image content block.
 *   - `document`: PDF, sent as Anthropic document content block.
 *   - `text-file`: SVG/HTML/CSS/TXT/MD/JSON, inlined into the user's text prompt
 *      under a `[Attached: name]` header so the model sees it as context.
 *
 * Anything else (Figma, Sketch, PSD, AI) is rejected at the composer with a
 * specific message — those formats are binary and Claude can't read them.
 */
export type Attachment = ImageAttachment | DocumentAttachment | TextFileAttachment;

export interface ImageAttachment {
  id: string;
  kind: 'image';
  /** base64 (no data: prefix) */
  data: string;
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
  /** for preview */
  dataUrl: string;
  bytes: number;
  name?: string;
}

export interface DocumentAttachment {
  id: string;
  kind: 'document';
  /** base64 (no data: prefix) */
  data: string;
  mediaType: 'application/pdf';
  name: string;
  bytes: number;
}

export interface TextFileAttachment {
  id: string;
  kind: 'text-file';
  /** UTF-8 source. SVG markup, CSS rules, etc. */
  content: string;
  /** e.g. 'image/svg+xml', 'text/html', 'text/css', 'application/json'. */
  mediaType: string;
  name: string;
  bytes: number;
}

export interface AppState {
  ready: boolean;
  projects: Project[];
  activeProjectId: string | null;
  messages: ChatMessage[];
  versions: Version[];
  /** Currently rendered version id (most recent for the project, or user-selected). */
  selectedVersionId: string | null;
  /** Logical current artifact (used by tweak parser, exporter). */
  artifactHtml: string;
  /** What's actually rendered in the iframe right now. Updates only on explicit reload events. */
  iframeSrcDoc: string;
  /** Bumped when iframeSrcDoc is replaced. Used as React key to force remount. */
  artifactRevision: number;
  isStreaming: boolean;
  deviceFrame: DeviceFrame;
  mode: CanvasMode;
  edits: EditRow[];
  comments: CommentRow[];
  tweakValues: TweakValue[];
  pendingComment: PendingComment | null;
  /** Optional draft text the chat composer should pick up next. */
  composerDraft: string;
  /** When true, full inline <script> source is sent to the model instead of the outline. */
  includeScript: boolean;
  attachments: Attachment[];
  streamProgress: StreamProgress | null;
  savedFiles: SavedFile[];

  init(): Promise<void>;
  createProject(): Promise<string>;
  importProject(input: { name?: string; html: string; css?: string }): Promise<string>;
  renameProject(id: string, name: string): Promise<void>;
  deleteProject(id: string): Promise<void>;
  setActiveProject(id: string): Promise<void>;
  sendMessage(text: string): Promise<void>;
  selectVersion(versionId: string): Promise<void>;
  revertToVersion(versionId: string): Promise<void>;
  setDeviceFrame(frame: DeviceFrame): void;
  /** Save user-edited HTML as a fresh version. No model round-trip. */
  saveCodeEdit(html: string): Promise<void>;
  setMode(mode: CanvasMode): void;
  setPendingComment(p: PendingComment | null): void;
  applyEdit(p: { selector: string; oldText: string; newText: string }): Promise<void>;
  /** Removes the most recent Edit row for the current version and re-applies the rest
   *  to the iframe via postMessage. Returns true if anything was undone. */
  undoLastEdit(): Promise<boolean>;
  saveComment(text: string): Promise<void>;
  setTweakValue(variable: string, value: string): Promise<void>;
  setComposerDraft(text: string): void;
  setIncludeScript(v: boolean): void;
  prefillComposer(text: string): void;
  addAttachment(a: Attachment): void;
  removeAttachment(id: string): void;
  stop(): void;
  /** Like stop() but additionally rolls back the in-flight version + chat turns
   *  so nothing partial is committed. Prior preview stays intact. */
  stopAndDiscard(): Promise<void>;
  /** Save a snapshot of the current design's HTML as a named saved file. */
  saveCurrentDesign(name?: string): Promise<void>;
  deleteSavedFile(id: string): Promise<void>;
  /** Create a new project pre-loaded with the HTML from a saved file. */
  newProjectFromSavedFile(fileId: string): Promise<string>;
}

/**
 * Lightweight pub-sub for streamed artifact chunks. The Canvas registers a callback
 * on mount so we can stream chunks straight into the iframe via `patch-append` rather
 * than re-srcdoc'ing on every flush (avoids the "blink every 250ms" regression).
 */
type ArtifactChunkListener = (chunk: string) => void;
const artifactChunkListeners = new Set<ArtifactChunkListener>();

export function subscribeArtifactChunks(cb: ArtifactChunkListener): () => void {
  artifactChunkListeners.add(cb);
  return () => artifactChunkListeners.delete(cb);
}

function emitArtifactChunk(chunk: string): void {
  for (const listener of artifactChunkListeners) listener(chunk);
}

interface InternalState {
  abortController: AbortController | null;
  initialized: boolean;
  /** Set by `stopAndDiscard` so the abort path drops the in-flight rows instead
   *  of persisting them as `partial`. Cleared in `finally`. */
  discardOnAbort: boolean;
}

const internal: InternalState = {
  abortController: null,
  initialized: false,
  discardOnAbort: false,
};

/** Auto-abort if zero upstream bytes arrive for this long. */
const STREAM_WATCHDOG_MS = 90_000;

/**
 * Edit turns regenerate the *full* document with most of it preserved verbatim. Opus
 * is overkill (and slow) for that workload — Sonnet does this faithfully and ~2-3×
 * faster, which compounds with prompt caching to make iteration feel responsive.
 *
 * Honored only when the user is on the default model. If they've explicitly chosen
 * something else in Settings (e.g. Haiku for cost), we respect that — they're driving.
 */
const EDIT_FAST_MODEL = 'claude-sonnet-4-6';


function makeProject(): Project {
  const now = Date.now();
  return {
    id: uuid(),
    name: 'Untitled',
    autoNamed: true,
    createdAt: now,
    updatedAt: now,
    currentVersionId: null,
  };
}

function deriveAutoName(text: string): string {
  const words = text.trim().split(/\s+/).slice(0, 6).join(' ');
  if (!words) return 'Untitled';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function dbMessageToChat(m: Message, status: ChatStatus): ChatMessage {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    createdAt: m.createdAt,
    status,
    versionId: m.versionId,
  };
}

function selectArtifactFor(version: Version | undefined): string {
  if (!version) return '';
  if (version.htmlFinal) return version.htmlFinal;
  return version.htmlPartial;
}

export const useApp = create<AppState>((set, get) => ({
  ready: false,
  projects: [],
  activeProjectId: null,
  messages: [],
  versions: [],
  selectedVersionId: null,
  artifactHtml: '',
  iframeSrcDoc: '',
  artifactRevision: 0,
  isStreaming: false,
  deviceFrame: 'desktop',
  mode: 'select',
  edits: [],
  comments: [],
  tweakValues: [],
  pendingComment: null,
  composerDraft: '',
  includeScript: false,
  attachments: [],
  streamProgress: null,
  savedFiles: [],

  async init() {
    if (internal.initialized) return;
    internal.initialized = true;
    const [projects, savedFiles] = await Promise.all([
      db().projects.orderBy('updatedAt').reverse().toArray(),
      db().savedFiles.orderBy('createdAt').reverse().toArray(),
    ]);
    set({ projects, savedFiles, ready: true });
    if (projects.length > 0) {
      await get().setActiveProject(projects[0]!.id);
    }
  },

  async createProject() {
    const project = makeProject();
    await db().projects.put(project);
    set((s) => ({ projects: [project, ...s.projects] }));
    await get().setActiveProject(project.id);
    return project.id;
  },

  async importProject({ name, html, css }) {
    const project = makeProject();
    if (name && name.trim()) {
      project.name = name.trim();
      project.autoNamed = false;
    } else {
      project.name = 'Imported HTML';
      project.autoNamed = false;
    }
    // If the file uses the __bundler envelope (claude.ai/design exports), unpack
    // it first — decode base64 assets, inline JS, replace font UUIDs with data
    // URIs. This avoids blob:null/ URL failures when files are opened via file://.
    const sourceHtml = isBundlerHtml(html) ? await unbundleHtml(html) : html;
    // Then merge with any optional CSS paste and resolve remaining external resources.
    const merged = mergeImportedHtml(sourceHtml, css);
    const contained = await selfContainHtml(merged);
    const finalHtml = contained.html;

    const versionId = uuid();
    const promptTurnId = uuid();
    const now = Date.now();
    const version: Version = {
      id: versionId,
      projectId: project.id,
      createdAt: now,
      parentVersionId: null,
      status: 'complete',
      htmlPartial: '',
      htmlFinal: finalHtml,
      modelId: 'imported',
      promptTurnId,
    };
    project.currentVersionId = versionId;
    project.updatedAt = now;

    const importNote = buildImportNote(contained);
    const importMessage: Message = {
      id: promptTurnId,
      projectId: project.id,
      versionId,
      role: 'system',
      createdAt: now,
      content: importNote,
    };
    await db().transaction('rw', [db().projects, db().versions, db().messages], async () => {
      await db().projects.put(project);
      await db().versions.put(version);
      await db().messages.put(importMessage);
    });
    set((s) => ({ projects: [project, ...s.projects] }));
    await get().setActiveProject(project.id);
    return project.id;
  },

  async renameProject(id, name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    await db().projects.update(id, { name: trimmed, autoNamed: false, updatedAt: Date.now() });
    set((s) => ({
      projects: s.projects.map((p) =>
        p.id === id ? { ...p, name: trimmed, autoNamed: false, updatedAt: Date.now() } : p,
      ),
    }));
  },

  async deleteProject(id) {
    await db().transaction(
      'rw',
      [db().projects, db().versions, db().messages, db().comments, db().edits, db().tweakValues],
      async () => {
        await db().projects.delete(id);
        await db().versions.where('projectId').equals(id).delete();
        await db().messages.where('projectId').equals(id).delete();
        await db().comments.where('projectId').equals(id).delete();
        await db().edits.where('projectId').equals(id).delete();
        await db().tweakValues.where('projectId').equals(id).delete();
      },
    );
    set((s) => {
      const next = s.projects.filter((p) => p.id !== id);
      const wasActive = s.activeProjectId === id;
      return {
        projects: next,
        activeProjectId: wasActive ? null : s.activeProjectId,
        messages: wasActive ? [] : s.messages,
        versions: wasActive ? [] : s.versions,
        selectedVersionId: wasActive ? null : s.selectedVersionId,
        artifactHtml: wasActive ? '' : s.artifactHtml,
        artifactRevision: s.artifactRevision + 1,
      };
    });
    if (get().activeProjectId === null && get().projects.length > 0) {
      await get().setActiveProject(get().projects[0]!.id);
    }
  },

  async setActiveProject(id) {
    const messages = await db()
      .messages.where('[projectId+createdAt]')
      .between([id, 0], [id, Infinity])
      .toArray();
    const versions = await db()
      .versions.where('[projectId+createdAt]')
      .between([id, 0], [id, Infinity])
      .toArray();
    const project = await db().projects.get(id);
    const selectedVersionId = project?.currentVersionId ?? versions[versions.length - 1]?.id ?? null;
    const selected = versions.find((v) => v.id === selectedVersionId);
    const messageStatuses = new Map<string, ChatStatus>();
    for (const v of versions) {
      // Map an assistant message to its version's status when displayable.
      const turn = v.promptTurnId;
      if (v.status === 'partial') messageStatuses.set(turn, 'partial');
      else if (v.status === 'cancelled') messageStatuses.set(turn, 'cancelled');
      else if (v.status === 'error') messageStatuses.set(turn, 'error');
    }
    const [edits, comments, tweakValues] = selectedVersionId
      ? await Promise.all([
          db().edits.where('versionId').equals(selectedVersionId).toArray(),
          db().comments.where('versionId').equals(selectedVersionId).toArray(),
          db().tweakValues.where('versionId').equals(selectedVersionId).toArray(),
        ])
      : [[], [], []];
    const html = selectArtifactFor(selected);
    set({
      activeProjectId: id,
      messages: messages.map((m) =>
        dbMessageToChat(
          m,
          m.role === 'assistant'
            ? messageStatuses.get(m.id) ?? 'complete'
            : 'complete',
        ),
      ),
      versions,
      selectedVersionId,
      artifactHtml: html,
      iframeSrcDoc: html,
      artifactRevision: get().artifactRevision + 1,
      edits,
      comments,
      tweakValues,
      mode: 'select',
      pendingComment: null,
      attachments: [],
    });
  },

  async sendMessage(text: string) {
    const trimmed = text.trim();
    const attachments = get().attachments;
    if ((!trimmed && attachments.length === 0) || get().isStreaming) return;

    const apiKey = window.localStorage.getItem(API_KEY_STORAGE_KEY) ?? '';
    const userChosenModel =
      window.localStorage.getItem(MODEL_STORAGE_KEY) || DEFAULT_MODEL;
    if (!apiKey) {
      const sys: ChatMessage = {
        id: uuid(),
        role: 'system',
        content: 'No API key. Open Settings, paste your Anthropic key, and click Test.',
        createdAt: Date.now(),
        status: 'error',
        versionId: null,
      };
      set((s) => ({ messages: [...s.messages, sys] }));
      return;
    }

    let projectId = get().activeProjectId;
    if (!projectId) projectId = await get().createProject();

    // Capture the version that's currently rendered in the canvas BEFORE we add the
    // new in-flight placeholder version — that's what we want Claude to edit.
    const priorSelectedVersionId = get().selectedVersionId;

    // Determine isEditing + model up front so the version row records the *actual*
    // model the request used.
    const priorVersion =
      priorSelectedVersionId
        ? get().versions.find((v) => v.id === priorSelectedVersionId)
        : null;
    // "yes, replace the whole design" is the explicit user opt-in to accept a full
    // regen after being warned. Treat it as a fresh generation, not an edit.
    const userConfirmedFullRegen = /replace\s+the\s+whole\s+design/i.test(trimmed);
    const isEditing = Boolean(priorVersion?.htmlFinal) && !userConfirmedFullRegen;
    const model =
      isEditing && userChosenModel === DEFAULT_MODEL ? EDIT_FAST_MODEL : userChosenModel;

    // Comments queued via the popover live as `status: 'draft'` until something
    // actually goes to the model. Promote them to 'sent' here so the chat-header
    // badge clears and the data model reflects what was just dispatched.
    const draftComments = priorSelectedVersionId
      ? get().comments.filter(
          (c) => c.versionId === priorSelectedVersionId && c.status === 'draft',
        )
      : [];

    const now = Date.now();
    const userMessage: Message = {
      id: uuid(),
      projectId,
      versionId: null,
      role: 'user',
      createdAt: now,
      content: trimmed,
    };
    const assistantId = uuid();
    const versionId = uuid();
    const assistantMessage: Message = {
      id: assistantId,
      projectId,
      versionId,
      role: 'assistant',
      createdAt: now + 1,
      content: '',
    };
    const version: Version = {
      id: versionId,
      projectId,
      createdAt: now + 2,
      parentVersionId: get().selectedVersionId,
      status: 'streaming',
      htmlPartial: '',
      htmlFinal: null,
      modelId: model,
      promptTurnId: assistantId,
    };

    await db().transaction(
      'rw',
      [db().messages, db().versions, db().projects, db().comments],
      async () => {
        await db().messages.bulkPut([userMessage, assistantMessage]);
        await db().versions.put(version);
        const proj = await db().projects.get(projectId!);
        if (proj) {
          const update: Partial<Project> = { updatedAt: Date.now() };
          if (proj.autoNamed && !proj.currentVersionId) {
            update.name = deriveAutoName(trimmed);
          }
          await db().projects.update(projectId!, update);
        }
        for (const c of draftComments) {
          await db().comments.update(c.id, {
            status: 'sent',
            sentInMessageId: assistantId,
          });
        }
      },
    );

    const userChat: ChatMessage = {
      id: userMessage.id,
      role: 'user',
      content: userMessage.content,
      createdAt: userMessage.createdAt,
      status: 'complete',
      versionId: null,
      ...(attachments.length > 0
        ? {
            attachments: attachments.map((a) => {
              if (a.kind === 'image') {
                return { kind: 'image' as const, dataUrl: a.dataUrl, mediaType: a.mediaType };
              }
              if (a.kind === 'document') {
                return { kind: 'document' as const, name: a.name, bytes: a.bytes };
              }
              return {
                kind: 'text-file' as const,
                name: a.name,
                mediaType: a.mediaType,
                bytes: a.bytes,
              };
            }),
          }
        : {}),
    };
    const assistantChat: ChatMessage = {
      id: assistantMessage.id,
      role: 'assistant',
      content: '',
      createdAt: assistantMessage.createdAt,
      status: 'streaming',
      versionId,
    };

    // Streaming visual policy:
    //   - If `isEditing` (prior design rendered), keep it visible — don't swap to
    //     a scaffold and don't push patches into it. The user sees the old preview
    //     until the new one is fully baked, then a single clean refresh.
    //   - For first-time generations there's nothing to keep, so we mount the
    //     streaming scaffold and let chunks fill it progressively.
    const startedAt = Date.now();
    set((s) => ({
      projects: s.projects.map((p) =>
        p.id === projectId
          ? {
              ...p,
              updatedAt: Date.now(),
              ...(p.autoNamed && !p.currentVersionId ? { name: deriveAutoName(trimmed) } : {}),
            }
          : p,
      ),
      messages: [...s.messages, userChat, assistantChat],
      versions: [...s.versions, version],
      comments: s.comments.map((c) =>
        draftComments.some((d) => d.id === c.id)
          ? { ...c, status: 'sent', sentInMessageId: assistantId }
          : c,
      ),
      // Don't change the on-screen selection while editing — the prior version stays
      // displayed. We commit selectedVersionId = versionId only when the new one is
      // ready (in the success path below).
      selectedVersionId: isEditing ? s.selectedVersionId : versionId,
      artifactHtml: isEditing ? s.artifactHtml : '',
      iframeSrcDoc: isEditing ? s.iframeSrcDoc : STREAMING_SCAFFOLD,
      artifactRevision: isEditing ? s.artifactRevision : s.artifactRevision + 1,
      isStreaming: true,
      attachments: [],
      streamProgress: { startedAt, lastBytesAt: startedAt, totalBytes: 0, model },
    }));

    const controller = new AbortController();
    internal.abortController = controller;

    // Watchdog: if upstream sends *zero* bytes for STREAM_WATCHDOG_MS, give up. Without
    // this, a hung Anthropic / proxy connection looks identical to a slow but fine
    // generation — UI stays "streaming…" indefinitely.
    let lastBytesAt = startedAt;
    let watchdogTripped: string | null = null;
    const watchdog = setInterval(() => {
      const idleFor = Date.now() - lastBytesAt;
      if (idleFor > STREAM_WATCHDOG_MS) {
        watchdogTripped = `No data from the model for ${Math.round(idleFor / 1000)}s.`;
        controller.abort();
      }
    }, 5_000);

    const extractor = new ArtifactExtractor();

    const updateAssistant = (mutate: (m: ChatMessage) => ChatMessage) =>
      set((s) => ({
        messages: s.messages.map((m) => (m.id === assistantId ? mutate(m) : m)),
      }));

    try {
      // Map our typed Attachment union into the shape buildApiMessages expects.
      const apiAttachments = attachments.map((a) =>
        a.kind === 'image'
          ? { kind: 'image' as const, data: a.data, mediaType: a.mediaType }
          : a.kind === 'document'
            ? { kind: 'document' as const, data: a.data, name: a.name }
            : {
                kind: 'text-file' as const,
                name: a.name,
                mediaType: a.mediaType,
                content: a.content,
              },
      );
      const messagesForApi = buildApiMessages(
        get().messages.filter((m) => m.id !== userMessage.id && m.id !== assistantId),
        get().versions,
        priorSelectedVersionId,
        trimmed,
        apiAttachments,
        { includeScript: get().includeScript },
      );

      const hasCurrentDesign = messagesForApi.some((m) =>
        typeof m.content === 'string'
          ? m.content.includes('<current_design>')
          : m.content.some((b) => b.type === 'text' && b.text.includes('<current_design>')),
      );
      // Edit mode whenever a current_design is in context (every iteration after the
      // first). The full-regen ARTIFACT_PRESERVATION_DIRECTIVE is no longer reachable
      // — we always embed via <current_design>, so structured edits are the contract.
      const systemForTurn = hasCurrentDesign
        ? SYSTEM_PROMPT_V1 + EDIT_MODE_DIRECTIVE
        : SYSTEM_PROMPT_V1;

      for await (const delta of streamTextDeltas({
        apiKey,
        model,
        system: systemForTurn,
        messages: messagesForApi,
        signal: controller.signal,
        onUpstreamBytes: (bytes) => {
          lastBytesAt = Date.now();
          set((s) =>
            s.streamProgress
              ? {
                  streamProgress: {
                    ...s.streamProgress,
                    lastBytesAt,
                    totalBytes: s.streamProgress.totalBytes + bytes,
                  },
                }
              : {},
          );
        },
      })) {
        const events = extractor.push(delta);
        let textAppended = '';
        for (const ev of events) {
          if (ev.type === 'text') textAppended += ev.chunk;
          else if (ev.type === 'artifact' && !isEditing) {
            // Only push live patches when there's no prior design to keep visible.
            emitArtifactChunk(ev.chunk);
          }
        }
        if (textAppended) {
          updateAssistant((m) => ({ ...m, content: m.content + textAppended }));
        }
      }

      for (const ev of extractor.flush()) {
        if (ev.type === 'text') updateAssistant((m) => ({ ...m, content: m.content + ev.chunk }));
        else if (ev.type === 'artifact' && !isEditing) emitArtifactChunk(ev.chunk);
      }

      // Resolve the final HTML, preferring structured edits over full-artifact regen.
      //   1. If the model emitted <edits>, parse + apply to the prior version's HTML.
      //   2. Else, if it emitted <artifact>, use that directly (full regen).
      //   3. Else, no design change — keep prior preview, just record the chat reply.
      const rawText = extractor.text();
      const editsBlock = extractEditsBlock(rawText);
      const priorHtml = priorVersion?.htmlFinal ?? '';
      let finalHtml = '';
      let resultSummary = '';
      let editOpsResult: ReturnType<typeof applyEdits> | null = null;
      let editOpsParsed = 0;
      if (editsBlock !== null && priorHtml) {
        const ops = parseEdits(editsBlock);
        editOpsParsed = ops.length;
        if (ops.length > 0) {
          editOpsResult = applyEdits(priorHtml, ops);
          finalHtml = editOpsResult.html;
        }
      }
      if (!finalHtml && extractor.hasArtifact()) {
        if (isEditing && priorHtml) {
          // In edit mode the model should never output a full <artifact> — it means
          // it ignored EDIT_MODE_DIRECTIVE and regenerated the whole document. Keep
          // the prior design intact and prompt the user to confirm explicitly.
          finalHtml = priorHtml;
          resultSummary =
            '⚠ Claude tried to replace the entire design instead of making a targeted edit. Your design was NOT changed. ' +
            'To apply Claude\'s regenerated version anyway, reply: **"yes, replace the whole design"**. ' +
            'Otherwise, rephrase your request more specifically (e.g. "change the hero title to …" or "remove the nav link that says …").';
        } else {
          finalHtml = extractor.artifact();
        }
      }
      if (!finalHtml && isEditing && priorHtml) {
        // Model emitted neither usable <edits> nor <artifact>. Keep prior design.
        finalHtml = priorHtml;
        if (editsBlock !== null && editOpsParsed === 0) {
          resultSummary =
            '⚠ Claude returned an <edits> block I could not parse. The design was not changed. Try rephrasing the prompt.';
        } else if (editsBlock === null) {
          resultSummary =
            '⚠ Claude did not return an <edits> block — the design was not changed. The model may have been chatting; rephrase as "remove/change/add ..." for a definite edit.';
        }
      }
      if (editOpsResult) {
        const { applied, skipped } = editOpsResult;
        if (applied > 0 && skipped.length === 0) {
          resultSummary = `Applied ${applied} edit${applied === 1 ? '' : 's'}.`;
        } else if (applied > 0 && skipped.length > 0) {
          const missList = skipped.map((s) => ('selector' in s.op ? `\`${s.op.selector}\`` : `"${s.op.find}"`)).join(', ');
          resultSummary =
            `Applied ${applied}, skipped ${skipped.length} (selector did not match: ${missList}).`;
        } else if (applied === 0 && skipped.length > 0) {
          const missList = skipped.map((s) => ('selector' in s.op ? `\`${s.op.selector}\`` : `"${s.op.find}"`)).join(', ');
          resultSummary =
            `⚠ No edits applied. Selectors did not match the current design: ${missList}. The element may have a different structure than expected — try clicking it again in Comment mode to capture a fresh selector.`;
        }
      }

      // The displayed assistant content is the chat text minus any <edits> XML; users
      // shouldn't see the structured payload.
      const cleanedText = stripEditsBlock(rawText);
      updateAssistant((m) => ({
        ...m,
        status: 'complete',
        content: resultSummary ? `${cleanedText}\n\n_${resultSummary}_` : cleanedText,
      }));

      // Stream end: commit the new version + swap the on-screen preview in a single
      // state update so the iframe goes prior → new in one frame, no transient blank.
      set((s) => ({
        artifactHtml: finalHtml,
        iframeSrcDoc: finalHtml || s.iframeSrcDoc,
        artifactRevision: s.artifactRevision + 1,
        selectedVersionId: versionId,
      }));
      const persistedAssistantContent = (
        resultSummary ? `${cleanedText}\n\n_${resultSummary}_` : cleanedText
      ).trim();
      await db().transaction('rw', [db().messages, db().versions, db().projects], async () => {
        await db().messages.update(assistantId, { content: persistedAssistantContent });
        await db().versions.update(versionId, {
          status: 'complete',
          htmlFinal: finalHtml,
          htmlPartial: '',
        });
        await db().projects.update(projectId!, {
          currentVersionId: versionId,
          updatedAt: Date.now(),
        });
      });
      set((s) => ({
        versions: s.versions.map((v) =>
          v.id === versionId
            ? { ...v, status: 'complete', htmlFinal: finalHtml, htmlPartial: '' }
            : v,
        ),
        projects: s.projects.map((p) =>
          p.id === projectId ? { ...p, currentVersionId: versionId, updatedAt: Date.now() } : p,
        ),
      }));

      // Carry tweak values forward whose CSS variables still appear in the new
      // artifact's manifest. Without this, every refinement turn silently resets the
      // user's slider/color choices to the model's declared defaults — which feels
      // like data loss when only one element was edited and the variable name is
      // identical across versions. Variables that were dropped (because the model
      // restructured the section that used them) get filtered out.
      try {
        const newManifest = finalHtml ? extractTweakManifest(finalHtml) : null;
        if (newManifest && priorSelectedVersionId) {
          const newControlNames = new Set(newManifest.controls.map((c) => c.name));
          const priorTweaks = await db()
            .tweakValues.where('versionId')
            .equals(priorSelectedVersionId)
            .toArray();
          const carried: TweakValue[] = priorTweaks
            .filter((t) => newControlNames.has(t.variable))
            .map((t) => ({
              id: uuid(),
              projectId: t.projectId,
              versionId,
              variable: t.variable,
              value: t.value,
              updatedAt: Date.now(),
            }));
          if (carried.length > 0) {
            await db().tweakValues.bulkPut(carried);
            set((s) => ({ tweakValues: [...s.tweakValues, ...carried] }));
          } else {
            // No carry-forward; clear stale (prior-version) tweaks from store state
            // so the panel reflects the new version accurately.
            set({ tweakValues: [] });
          }
        } else {
          set({ tweakValues: [] });
        }
      } catch {
        // Carry-forward is best-effort. A bad manifest shouldn't break the success path.
      }
    } catch (err) {
      const aborted =
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && err.name === 'AbortError');
      const text = extractor.text().trim();
      const html = extractor.artifact();
      if (aborted) {
        // stopAndDiscard path: roll the in-flight artefacts back rather than commit
        // them as `partial`. The user wanted to throw this attempt away.
        if (internal.discardOnAbort) {
          await db().transaction(
            'rw',
            [db().messages, db().versions, db().comments],
            async () => {
              await db().messages.bulkDelete([userMessage.id, assistantId]);
              await db().versions.delete(versionId);
              // Restore the queued comments that were promoted to 'sent' on send.
              if (draftComments.length > 0) {
                await Promise.all(
                  draftComments.map((c) =>
                    db().comments.update(c.id, { status: 'draft', sentInMessageId: undefined }),
                  ),
                );
              }
            },
          );
          set((s) => ({
            messages: s.messages.filter(
              (m) => m.id !== userMessage.id && m.id !== assistantId,
            ),
            versions: s.versions.filter((v) => v.id !== versionId),
            comments: s.comments.map((c) =>
              draftComments.some((d) => d.id === c.id)
                ? { ...c, status: 'draft', sentInMessageId: undefined }
                : c,
            ),
            // Prior selection stays put; iframe stays on prior preview.
            selectedVersionId: priorSelectedVersionId ?? s.selectedVersionId,
          }));
        } else {
          const status: 'partial' | 'cancelled' = extractor.hasArtifact() ? 'partial' : 'cancelled';
          updateAssistant((m) => ({ ...m, status }));
          // In edit mode the prior version stays displayed — don't swap to a partial.
          // For a fresh first-time stream there's nothing to keep, so we surface what
          // we have.
          if (!isEditing && extractor.hasArtifact()) {
            set((s) => ({
              artifactHtml: html,
              iframeSrcDoc: html,
              artifactRevision: s.artifactRevision + 1,
              selectedVersionId: versionId,
            }));
          }
          await db().transaction('rw', [db().messages, db().versions, db().projects], async () => {
            await db().messages.update(assistantId, { content: text });
            await db().versions.update(versionId, {
              status,
              htmlPartial: html,
              htmlFinal: extractor.hasArtifact() ? html : null,
            });
            if (extractor.hasArtifact()) {
              await db().projects.update(projectId!, {
                currentVersionId: versionId,
                updatedAt: Date.now(),
              });
            }
          });
          set((s) => ({
            versions: s.versions.map((v) =>
              v.id === versionId
                ? { ...v, status, htmlPartial: html, htmlFinal: extractor.hasArtifact() ? html : null }
                : v,
            ),
            projects: extractor.hasArtifact()
              ? s.projects.map((p) =>
                  p.id === projectId ? { ...p, currentVersionId: versionId, updatedAt: Date.now() } : p,
                )
              : s.projects,
          }));
        }
      } else {
        const errorMessage = watchdogTripped
          ? `${watchdogTripped} The model may be overloaded or the prompt may be too large. Try again, switch model in Settings, or stop and rephrase.`
          : err instanceof ProxyUnreachableError
            ? `${err.message} Run \`pnpm dev\` (or \`pnpm dev:proxy\` alongside your existing Vite). The proxy listens on http://localhost:8787 and is required to reach Anthropic.`
            : err instanceof StreamError
              ? `${err.message}: ${tryParseAnthropicError(err.bodyText) ?? err.bodyText}`
              : err instanceof Error
                ? err.message
                : 'Unknown stream error.';
        updateAssistant((m) => ({
          ...m,
          status: 'error',
          content: m.content || errorMessage,
        }));
        await db().transaction('rw', [db().messages, db().versions], async () => {
          await db().messages.update(assistantId, { content: text || errorMessage });
          await db().versions.update(versionId, {
            status: 'error',
            errorMessage,
            htmlPartial: html,
          });
        });
        set((s) => ({
          versions: s.versions.map((v) =>
            v.id === versionId
              ? { ...v, status: 'error', errorMessage, htmlPartial: html }
              : v,
          ),
        }));
      }
    } finally {
      clearInterval(watchdog);
      internal.abortController = null;
      internal.discardOnAbort = false;
      set({ isStreaming: false, streamProgress: null });
    }
  },

  async selectVersion(versionId: string) {
    const v = get().versions.find((x) => x.id === versionId);
    if (!v) return;
    const [edits, comments, tweakValues] = await Promise.all([
      db().edits.where('versionId').equals(versionId).toArray(),
      db().comments.where('versionId').equals(versionId).toArray(),
      db().tweakValues.where('versionId').equals(versionId).toArray(),
    ]);
    set((s) => ({
      selectedVersionId: versionId,
      artifactHtml: selectArtifactFor(v),
      artifactRevision: s.artifactRevision + 1,
      edits,
      comments,
      tweakValues,
      pendingComment: null,
    }));
  },

  async revertToVersion(versionId: string) {
    const project = get().activeProjectId;
    const source = get().versions.find((v) => v.id === versionId);
    if (!project || !source || !source.htmlFinal) return;
    const now = Date.now();
    const fresh: Version = {
      id: uuid(),
      projectId: project,
      createdAt: now,
      parentVersionId: versionId,
      status: 'complete',
      htmlPartial: '',
      htmlFinal: source.htmlFinal,
      modelId: source.modelId,
      promptTurnId: uuid(),
    };
    await db().transaction('rw', [db().versions, db().projects], async () => {
      await db().versions.put(fresh);
      await db().projects.update(project, {
        currentVersionId: fresh.id,
        updatedAt: now,
      });
    });
    set((s) => ({
      versions: [...s.versions, fresh],
      selectedVersionId: fresh.id,
      artifactHtml: fresh.htmlFinal ?? '',
      artifactRevision: s.artifactRevision + 1,
      projects: s.projects.map((p) =>
        p.id === project ? { ...p, currentVersionId: fresh.id, updatedAt: now } : p,
      ),
    }));
  },

  setDeviceFrame(frame) {
    set({ deviceFrame: frame });
  },

  async saveCodeEdit(html) {
    const projectId = get().activeProjectId;
    if (!projectId) return;
    const trimmed = html.trim();
    if (!trimmed) return;
    const priorVersionId = get().selectedVersionId;
    const now = Date.now();
    const versionId = uuid();
    const promptTurnId = uuid();
    const newVersion: Version = {
      id: versionId,
      projectId,
      createdAt: now,
      parentVersionId: priorVersionId,
      status: 'complete',
      htmlPartial: '',
      htmlFinal: trimmed,
      modelId: 'manual-edit',
      promptTurnId,
    };
    const note: Message = {
      id: promptTurnId,
      projectId,
      versionId,
      role: 'system',
      createdAt: now,
      content: 'Manual code edit (Code mode). No model call.',
    };
    await db().transaction('rw', [db().versions, db().messages, db().projects], async () => {
      await db().versions.put(newVersion);
      await db().messages.put(note);
      await db().projects.update(projectId, {
        currentVersionId: versionId,
        updatedAt: now,
      });
    });
    set((s) => ({
      versions: [...s.versions, newVersion],
      messages: [
        ...s.messages,
        {
          id: note.id,
          role: 'system',
          content: note.content,
          createdAt: note.createdAt,
          status: 'complete',
          versionId,
        },
      ],
      projects: s.projects.map((p) =>
        p.id === projectId ? { ...p, currentVersionId: versionId, updatedAt: now } : p,
      ),
      selectedVersionId: versionId,
      artifactHtml: trimmed,
      iframeSrcDoc: trimmed,
      artifactRevision: s.artifactRevision + 1,
      // Reset overlays — these were tied to the prior version.
      edits: [],
      tweakValues: [],
    }));
  },

  setMode(mode) {
    set({ mode, pendingComment: mode === 'comment' ? get().pendingComment : null });
  },

  setPendingComment(pendingComment) {
    set({ pendingComment });
  },

  async applyEdit({ selector, oldText, newText }) {
    const versionId = get().selectedVersionId;
    const projectId = get().activeProjectId;
    if (!versionId || !projectId) return;
    const existing = get().edits.find((e) => e.selector === selector);
    if (existing) {
      const updated: EditRow = { ...existing, newText, createdAt: Date.now() };
      await db().edits.put(updated);
      set((s) => ({ edits: s.edits.map((e) => (e.id === existing.id ? updated : e)) }));
      return;
    }
    const row: EditRow = {
      id: uuid(),
      projectId,
      versionId,
      selector,
      oldText,
      newText,
      createdAt: Date.now(),
    };
    await db().edits.put(row);
    set((s) => ({ edits: [...s.edits, row] }));
  },

  async undoLastEdit() {
    const versionId = get().selectedVersionId;
    if (!versionId) return false;
    const versionEdits = get()
      .edits.filter((e) => e.versionId === versionId)
      .sort((a, b) => a.createdAt - b.createdAt);
    const last = versionEdits[versionEdits.length - 1];
    if (!last) return false;
    await db().edits.delete(last.id);
    set((s) => ({ edits: s.edits.filter((e) => e.id !== last.id) }));
    // The Canvas's `useEffect([edits, sendToIframe])` already reapplies the
    // remaining edit set on the next render. The iframe's existing applied edit
    // for this selector won't auto-revert though, so we restore the original text
    // explicitly via `apply-edits` with `newText: last.oldText` for the undone
    // selector. (The next full reload fully resets state.)
    // For simplicity we let the next iframe reload reset state; the user can
    // also re-edit if they need an immediate visual revert.
    return true;
  },

  async saveComment(text) {
    const pending = get().pendingComment;
    const versionId = get().selectedVersionId;
    const projectId = get().activeProjectId;
    if (!pending || !versionId || !projectId) return;
    const row: CommentRow = {
      id: uuid(),
      projectId,
      versionId,
      selector: pending.selector,
      snippet: pending.snippet,
      text: text.trim(),
      status: 'draft',
      createdAt: Date.now(),
    };
    await db().comments.put(row);
    set((s) => ({
      comments: [...s.comments, row],
      pendingComment: null,
    }));
    const composerLine = `Targeted change on element matching \`${pending.selector}\`: ${text.trim()}`;
    set((s) => ({
      composerDraft: s.composerDraft ? `${s.composerDraft}\n${composerLine}` : composerLine,
    }));
  },

  async setTweakValue(variable, value) {
    const versionId = get().selectedVersionId;
    const projectId = get().activeProjectId;
    if (!versionId || !projectId) return;
    const existing = get().tweakValues.find((t) => t.variable === variable);
    const row: TweakValue = existing
      ? { ...existing, value, updatedAt: Date.now() }
      : {
          id: uuid(),
          projectId,
          versionId,
          variable,
          value,
          updatedAt: Date.now(),
        };
    set((s) => ({
      tweakValues: existing
        ? s.tweakValues.map((t) => (t.id === existing.id ? row : t))
        : [...s.tweakValues, row],
    }));
    void db().tweakValues.put(row);
  },

  setComposerDraft(text) {
    set({ composerDraft: text });
  },

  setIncludeScript(v) {
    set({ includeScript: v });
  },

  prefillComposer(text) {
    set((s) => ({ composerDraft: s.composerDraft ? `${s.composerDraft}\n${text}` : text }));
  },

  addAttachment(a) {
    set((s) => ({ attachments: [...s.attachments, a] }));
  },

  removeAttachment(id) {
    set((s) => ({ attachments: s.attachments.filter((a) => a.id !== id) }));
  },

  stop() {
    internal.abortController?.abort();
  },

  async stopAndDiscard() {
    // Mark intent so the abort path knows to clean up rather than commit a partial.
    internal.discardOnAbort = true;
    internal.abortController?.abort();
  },

  async saveCurrentDesign(name) {
    const state = get();
    const selectedVersion = state.versions.find((v) => v.id === state.selectedVersionId);
    const html = selectedVersion?.htmlFinal ?? state.artifactHtml;
    if (!html) return;

    const activeProject = state.projects.find((p) => p.id === state.activeProjectId);
    const fileName = name?.trim() || activeProject?.name || 'Saved Design';
    const now = Date.now();
    const file: SavedFile = {
      id: uuid(),
      name: fileName,
      html,
      sourceProjectId: state.activeProjectId,
      sourceVersionId: state.selectedVersionId,
      createdAt: now,
    };
    await db().savedFiles.put(file);
    set((s) => ({ savedFiles: [file, ...s.savedFiles] }));
  },

  async deleteSavedFile(id) {
    await db().savedFiles.delete(id);
    set((s) => ({ savedFiles: s.savedFiles.filter((f) => f.id !== id) }));
  },

  async newProjectFromSavedFile(fileId) {
    const file = get().savedFiles.find((f) => f.id === fileId);
    if (!file) return '';
    return get().importProject({ name: file.name, html: file.html });
  },
}));


